import { domain, State } from 'modules/data';
import { integrationActions } from 'modules/integration';
import { normalize } from 'normalizr';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useUnmount } from 'react-use';
import { useGetReports } from 'shared/data/webApp/v1';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';
import { getDefaultPath, getPath, Path } from 'urlBuilder';

import { leavePage, loadPageData, showDashboard } from '../../../resources/actions';
import reportConfigSchema from '../../../resources/data/reportConfig/reportConfigSchema';
import { getCustomFields, getReportSelectorTypes } from '../../../resources/selectors/reportConfigSelectors';
import { pageConfig } from '../../config';

export const usePageData = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const company = useCompanyBasedOnUrl();

    const reportType = domain.ReportType.LineItem;
    const integrationType = company.integration?.integrationType;
    const reportCodes = getReportSelectorTypes(reportType, integrationType);

    const { data, isInitialLoading, isSuccess, isError, isFetching } = useGetReports({
        query: {
            reportCodes,
            companyId: company.id,
        },
    });

    const entities = useMemo(() => {
        return normalize({ ...data, reportType, companyId: company.id }, { data: [reportConfigSchema] }).entities;
    }, [company.id, data, reportType]);

    const initFields = useCallback(() => {
        dispatch(
            integrationActions.loadAllCompanyFields({
                companyId: company.id,
                callback: (state: State) => {
                    const fields = getCustomFields(state, { reportType, companyId: company.id, integrationType });

                    dispatch(
                        loadPageData({
                            companyId: company.id,
                            entities,
                            reportType,
                            innerActivePageId: pageConfig.innerActivePageId,
                            fields,
                        })
                    );
                    dispatch(showDashboard());
                },
            })
        );
    }, [company.id, dispatch, entities, integrationType, reportType]);

    useEffect(() => {
        if (!isFetching && isSuccess) {
            initFields();
        }
    }, [isSuccess, initFields, isFetching]);

    useEffect(() => {
        if (isError) {
            const path = company.id ? getPath(Path.reportsDashboard, company.id, reportType) : getDefaultPath();

            history.push(path);
        }
    }, [company.id, history, isError, reportType]);

    useUnmount(() => {
        dispatch(leavePage());
    });

    return { isInitialLoading, company };
};
